import {Component, Inject, PLATFORM_ID} from '@angular/core';
import {HeaderComponent} from '../../components/header/header.component';
import {Footer} from '../../../core/components/footer/footer';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {GiftCardService} from '../../services/gift-card/gift-card.service';
import {ToastrService} from 'ngx-toastr';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {isPlatformBrowser, NgClass, NgIf} from '@angular/common';
import {TiltDirective} from '../../../core/directives/tilt.directive';
import {DateTime} from 'luxon';
@Component({
  selector: 'app-gift-card',
  standalone: true,
  imports: [
    HeaderComponent,
    Footer,
    ReactiveFormsModule,
    TranslocoPipe,
    NgIf,
    NgClass,
    TiltDirective
  ],
  templateUrl: './gift-card.component.html',
  styleUrl: './gift-card.component.scss'
})
export class GiftCardComponent {
  public readonly minAmount = 20;
  public readonly maxAmount = 1000;
  public readonly amountStep = 5;
  public readonly todayIso = DateTime.now().toISODate() ?? DateTime.now().toFormat('yyyy-LL-dd');
  public isSubmitting = false;
  public form: FormGroup;
  public activeSection = 0;
  public constructor(
    private readonly fb: FormBuilder,
    private readonly giftCardService: GiftCardService,
    private readonly toastr: ToastrService,
    private readonly transloco: TranslocoService,
    private readonly authenticationService: AuthenticationService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
  ) {
    const currentUser = this.authenticationService.getCurrentUser();
    const buyerEmail = currentUser?.email ?? '';
    const buyerFirstName = currentUser?.firstname ?? '';
    this.form = this.fb.group({
      buyerFirstName: [buyerFirstName, [Validators.required, Validators.maxLength(255)]],
      buyerEmail: [buyerEmail, [Validators.required, Validators.email]],
      recipientEmail: ['', [Validators.required, Validators.email]],
      recipientFirstName: ['', [Validators.maxLength(255)]],
      deliveryDate: [''],
      amount: [50, [Validators.required, Validators.min(this.minAmount), Validators.max(this.maxAmount)]],
      message: [''],
      termsAccepted: [false, [Validators.requiredTrue]],
    });

  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  public submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastr.error(
        this.transloco.translate('marketplace.gift-card.form.error.content'),
        this.transloco.translate('marketplace.gift-card.form.error.title'),
      );
      return;
    }

    this.isSubmitting = true;
    const value = this.form.getRawValue();
    const parsedDeliveryDate = value.deliveryDate ? DateTime.fromISO(value.deliveryDate) : null;
    const deliveryDateIso = parsedDeliveryDate && parsedDeliveryDate.isValid
      ? parsedDeliveryDate.startOf('day').toISO() ?? undefined
      : undefined;

    this.giftCardService.purchaseGiftCard({
      buyerEmail: value.buyerEmail?.trim() ?? '',
      senderFirstName: value.buyerFirstName?.trim() || undefined,
      recipientEmail: value.recipientEmail?.trim() ?? '',
      recipientFirstName: value.recipientFirstName?.trim() || undefined,
      deliveryDate: deliveryDateIso,
      amount: Number(value.amount),
      message: value.message?.trim() || undefined,
    }).subscribe({
      next: (session) => {
        this.isSubmitting = false;

        this.toastr.success(
          this.transloco.translate('marketplace.gift-card.form.success.content'),
          this.transloco.translate('marketplace.gift-card.form.success.title'),
          {timeOut: 2000}
        );

        if (this.isBrowser && session.checkoutUrl) {
          window.location.href = session.checkoutUrl;
          return;
        }

        this.toastr.warning(
          this.transloco.translate('marketplace.gift-card.form.error.missing-checkout'),
          this.transloco.translate('marketplace.gift-card.form.error.title')
        );
      },
      error: (error) => {
        this.isSubmitting = false;
        const errorMessage =
          error?.error?.error ??
          this.transloco.translate('marketplace.gift-card.form.error.content');

        this.toastr.error(
          errorMessage,
          this.transloco.translate('marketplace.gift-card.form.error.title'),
        );
      }
    });
  }

  public showError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  public openSection(index: number): void {
    if (!this.isSectionUnlocked(index)) {
      return;
    }
    this.activeSection = index;
  }

  public isSectionActive(index: number): boolean {
    return this.activeSection === index;
  }

  public getSectionStatusLabel(index: number): string {
    const state = this.getSectionState(index);
    return `marketplace.gift-card.form.section.status.${state}`;
  }

  public isSectionUnlocked(index: number): boolean {
    if (index === 0) {
      return true;
    }
    return this.isSectionComplete(index - 1);
  }

  private isSectionComplete(index: number): boolean {
    switch (index) {
      case 0: {
        const firstName = this.form.get('buyerFirstName');
        const email = this.form.get('buyerEmail');
        return !!firstName?.value?.trim() && !!email?.valid;
      }
      case 1: {
        const recipientEmail = this.form.get('recipientEmail');
        return !!recipientEmail?.valid;
      }
      default:
        return false;
    }
  }

  private getSectionState(index: number): 'locked' | 'current' | 'completed' | 'ready' {
    if (!this.isSectionUnlocked(index)) {
      return 'locked';
    }
    if (this.activeSection === index) {
      return 'current';
    }
    if (index < this.activeSection) {
      return 'completed';
    }
    return 'ready';
  }

}
